import {
	KNOWN_CAMERA_OBJECT_NAMES,
	getCameraObjectIcon,
	getCameraObjectLabel,
	normalizeCameraObjectName,
} from 'src/components/Camera/helpers/camera-objects';
import type { SceneConditionTransform } from 'src/types/ScenesConditionsTypes';

export const CAMERA_OBJECTS_TRANSFORM_METHOD = 'cam_recognition';

/**
 * Object classes offered by the wizard until the backend vocabulary has loaded
 * (see `loadCameraObjectNames`). The model reports a wider, backend-owned set, so a
 * stored condition may legitimately reference a class that is not listed here.
 */
export const CAMERA_OBJECT_TYPES = KNOWN_CAMERA_OBJECT_NAMES;

export type CameraObjectType = string;

/** Ranges the wizard works in. `minscore` is whole percent, as entered and displayed. */
export const CAMERA_OBJECTS_LIMITS = {
	nobjs: { min: 1, max: 20, step: 1, default: 1 },
	minscore: { min: 20, max: 100, step: 5, default: 55 },
} as const;

/**
 * The API carries the confidence as a 0-1 ratio, so it is converted at the transform
 * boundary and every caller above it stays in percent.
 */
const percentToRatio = (percent: unknown): number => Number(percent) / 100;

const ratioToPercent = (ratio: unknown): number => Math.round(Number(ratio) * 100);

export type CameraObjectsParam = keyof typeof CAMERA_OBJECTS_LIMITS;

export type CameraObjectTypeOption = {
	title: string;
	value: CameraObjectType;
	icon: string;
};

const isCameraObjectType = (type: unknown): type is CameraObjectType =>
	normalizeCameraObjectName(type) !== '';

/*
 * Object type tokens shown for the objects condition.
 *
 * `selectedType` is appended when the available classes do not contain it, so
 * editing a condition that references a class the backend no longer reports still
 * shows, and keeps, the stored selection.
 *
 * @param availableTypes - The classes to offer, defaulting to the ones we ship.
 * @param selectedType - The class the edited condition currently triggers on.
 * @returns A token per offered class, translated and carrying its icon.
 */
export const getCameraObjectTypeOptions = (
	availableTypes: string[] = CAMERA_OBJECT_TYPES,
	selectedType?: string,
): CameraObjectTypeOption[] => {
	const selected = normalizeCameraObjectName(selectedType);
	const isSelectedMissing = Boolean(selected) && !availableTypes.includes(selected);
	const types = isSelectedMissing ? [...availableTypes, selected] : availableTypes;

	return types.map((type) => ({
		title: getCameraObjectLabel(type),
		value: type,
		icon: getCameraObjectIcon(type),
	}));
};

/**
 * Keep an objects parameter inside its supported range, falling back to the
 * default when the input is not a finite number (e.g. a cleared input field).
 */
export const clampCameraObjectsParam = (param: CameraObjectsParam, value: unknown): number => {
	const { min, max, default: fallback } = CAMERA_OBJECTS_LIMITS[param];
	const parsed = Math.round(Number(value));
	if (!Number.isFinite(parsed)) {
		return fallback;
	}
	return Math.min(max, Math.max(min, parsed));
};

export const isCameraObjectsParamInRange = (param: CameraObjectsParam, value: unknown): boolean => {
	const { min, max } = CAMERA_OBJECTS_LIMITS[param];
	const parsed = Number(value);
	return Number.isFinite(parsed) && parsed >= min && parsed <= max;
};

/**
 * Build the IoTS `transform` payload for the camera objects condition. Numbers are
 * passed through as typed so `validateCameraCondition` can reject out-of-range input
 * instead of silently correcting what the user entered.
 *
 * @param minscore - Minimum confidence in percent.
 */
export const buildCameraObjectsTransform = (
	type: string,
	nobjs: unknown,
	minscore: unknown,
): SceneConditionTransform => ({
	method: CAMERA_OBJECTS_TRANSFORM_METHOD,
	params: {
		type: normalizeCameraObjectName(type) || CAMERA_OBJECT_TYPES[0],
		nobjs: Number(nobjs),
		minscore: percentToRatio(minscore),
	},
});

/**
 * Read objects parameters out of a stored transform, defaulting anything missing.
 * An unrecognised class name is preserved rather than rewritten, so opening an
 * existing condition never silently changes which object it triggers on.
 *
 * @returns The parameters in wizard units, with the confidence back in percent.
 */
export const extractCameraObjectsParams = (
	transform?: SceneConditionTransform | null,
): { type: CameraObjectType; nobjs: number; minscore: number } => {
	const params = transform?.params ?? {};
	return {
		type: normalizeCameraObjectName(params.type) || CAMERA_OBJECT_TYPES[0],
		nobjs: clampCameraObjectsParam('nobjs', params.nobjs),
		minscore: clampCameraObjectsParam('minscore', ratioToPercent(params.minscore)),
	};
};

export const isCameraObjectsTransform = (transform?: SceneConditionTransform | null): boolean => {
	return transform?.method === CAMERA_OBJECTS_TRANSFORM_METHOD;
};

/**
 * A camera objects condition is complete once the transform carries an object type
 * and both numeric parameters sit inside their supported ranges.
 */
export const isCameraObjectsTransformValid = (transform?: SceneConditionTransform | null): boolean => {
	const params = transform?.params;
	return (
		isCameraObjectsTransform(transform) &&
		isCameraObjectType(params?.type) &&
		isCameraObjectsParamInRange('nobjs', params?.nobjs) &&
		isCameraObjectsParamInRange('minscore', ratioToPercent(params?.minscore))
	);
};
